import classNames from "classnames";
import React from "react";

// Base classes for BoxWrapper
const baseClasses = [
  "w-full",
  "bg-white",
  "border-none",
  "rounded-[var(--corner)]",
  "mb-[var(--gap)]",
  "overflow-x-hidden",
  "min-h-[130px]",
];

// Variant classes
const variantClasses = {
  expandable: "!min-h-0",
  fullHeight: "h-full",
  roundBottom: "!rounded-t-none !rounded-b-[var(--corner)]",
  roundTop: "!rounded-b-none !rounded-t-[var(--corner)]",
  middle: "!rounded-none",
  bordered: "!border !border-solid !border-[#e9e9e9]",
  lastChild: "last:mb-0",
};

// Box content classes
const boxContentClasses = "p-[var(--gap)]";
const boxContentTableModeClasses = "!pb-0";
const boxContentFullWidthClasses = "!px-0";
const boxContentCenterClasses = "flex justify-center items-center !px-0";
const boxContentTopPaddingClasses = "!pt-[var(--margin-small)]";

// Full width classes
const fullWidthClasses = "w-full px-0";

// Plain classes (for nested text)
const plainClasses = "[&_h3]:text-base [&_h3]:text-black [&_p]:text-black";

// Table mode classes
const tableModeClasses = "rounded-t-[var(--corner)] rounded-b-none";

// View row first child
const viewRowFirstChildClasses = "[&_.box-content>.view-row:first-child]:mt-[var(--gap-extra-small)]";

// Box footer
const boxFooterClasses = "border-t border-[#f0f0f0]";

// Border div bottom
const borderDivBottomClasses = "[&_.border-div-bottom]:-mt-px";

interface BoxWrapperProps {
  children: React.ReactNode;
  className?: string;
  expandable?: boolean;
  fullHeight?: boolean;
  roundBottom?: boolean;
  roundTop?: boolean;
  middle?: boolean;
  plain?: boolean;
  fullWidth?: boolean;
  contentCenter?: boolean;
  topPadding?: boolean;
  tableMode?: boolean;
  bordered?: boolean;
}

function BoxWrapper({ children, className, ...props }: BoxWrapperProps) {
  const wrapperClasses = classNames(
    baseClasses,
    viewRowFirstChildClasses,
    borderDivBottomClasses,
    variantClasses.lastChild,
    props.expandable && variantClasses.expandable,
    props.fullHeight && variantClasses.fullHeight,
    props.roundBottom && variantClasses.roundBottom,
    props.roundTop && variantClasses.roundTop,
    props.middle && variantClasses.middle,
    props.plain && plainClasses,
    props.fullWidth && fullWidthClasses,
    props.tableMode && tableModeClasses,
    props.bordered && variantClasses.bordered,
    className
  );

  return <div className={wrapperClasses}>{children}</div>;
}

// Export helper function to get box-content classes
function getBoxContentClasses(props: {
  tableMode?: boolean;
  fullWidth?: boolean;
  contentCenter?: boolean;
  topPadding?: boolean;
}) {
  return classNames(
    boxContentClasses,
    props.tableMode && boxContentTableModeClasses,
    props.fullWidth && boxContentFullWidthClasses,
    props.contentCenter && boxContentCenterClasses,
    props.topPadding && boxContentTopPaddingClasses
  );
}

export { BoxWrapper, getBoxContentClasses, boxFooterClasses };
